/**
 * Единый lifecycle Р7 для dialog и inline preview (WUI-492).
 *
 * open → DS-фаза не меняется (запоминаем preOpenDsPhase)
 * dirty → editing (бейдж), редактор доступен
 * close без dirty → restore preOpenDsPhase
 * close с dirty → awaiting_ws + timer → WS/sync-state → staged|error|restore
 * view → editing/awaiting_ws не ставятся
 *
 * @see dev/contracts/attachments.md — «Фазы бейджа: open / dirty / close»
 */
import type { FileItem } from '@/common/types/fileItem'
import { getDsAttachmentStore } from '@/common/composables/useDsAttachmentState'
import type { DsAttachmentPhase } from '@/common/utils/dsAttachmentPhases'
import {
  findSessionForAttachmentItem,
  onEditorClosed,
  resolveEditingSessionForClose,
  setSessionWasDirty,
  type R7AttachmentEditSession,
} from '@/common/utils/r7AttachmentEditSession'

export type R7EditorLifecycleCloseResult = {
  awaitingWs: boolean
  session: R7AttachmentEditSession | null
  restoredPhase: DsAttachmentPhase | null
}

/** Фаза DS до open — для restore при close без dirty. */
export function capturePreOpenDsPhase(
  item: FileItem,
  elementId: number,
): DsAttachmentPhase {
  try {
    const phase = getDsAttachmentStore(elementId).getPhase(item)

    // Стабильные фазы DS — источник правды при (re)open; не перетирать устаревшим session.preOpen.
    if (
      phase === 'staged'
      || phase === 'idle'
      || phase === 'error'
      || phase === 'discarded'
    ) {
      return phase
    }

    const existingSession = findSessionForAttachmentItem(item, elementId)
    const sessionPreOpen = existingSession?.preOpenDsPhase

    if (phase === 'none') {
      if (
        sessionPreOpen
        && sessionPreOpen !== 'none'
        && sessionPreOpen !== 'editing'
        && sessionPreOpen !== 'downloading'
        && sessionPreOpen !== 'awaiting_ws'
      ) {
        return sessionPreOpen
      }
      return 'idle'
    }

    // Transient busy: восстановить pre-open из сессии, если был staged до editing.
    if (
      sessionPreOpen
      && sessionPreOpen !== 'none'
      && sessionPreOpen !== 'editing'
      && sessionPreOpen !== 'downloading'
      && sessionPreOpen !== 'awaiting_ws'
    ) {
      return sessionPreOpen
    }
    return 'idle'
  } catch {
    return 'idle'
  }
}

/**
 * `onDocumentStateChange(true)`: wasDirty + DS `editing`.
 * View-only — no-op.
 */
export function markR7AttachmentDocumentDirty(params: {
  item: FileItem
  elementId: number
  documentKey: string | null | undefined
}): void {
  const key = String(params.documentKey ?? '').trim()
  if (!key) return
  const session = resolveEditingSessionForClose(key, params.item, params.elementId)
  if (!session || session.editorMode === 'view' || session.abandoned || session.phase === 'error') {
    return
  }
  setSessionWasDirty(key)
  session.phase = 'editing'
  try {
    const store = getDsAttachmentStore(params.elementId)
    if (store.hasSnapshot(params.item)) {
      store.setPhase(params.item, 'editing')
    }
  } catch {
    // ignore
  }
}

function restoreDsPhaseAfterCleanClose(
  item: FileItem,
  elementId: number,
  preOpen: DsAttachmentPhase | undefined,
): DsAttachmentPhase {
  const target: DsAttachmentPhase =
    !preOpen || preOpen === 'none' || preOpen === 'editing' || preOpen === 'downloading'
      ? 'idle'
      : preOpen === 'awaiting_ws'
        ? 'idle'
        : preOpen
  try {
    const store = getDsAttachmentStore(elementId)
    if (store.hasSnapshot(item)) {
      store.setPhase(item, target)
    }
  } catch {
    // ignore
  }
  return target
}

/**
 * Закрытие DocEditor (dialog X / preview destroy / смена файла на ленте).
 * Dirty → awaiting_ws + timer; иначе restore preOpenDsPhase на DS.
 */
export function finalizeR7AttachmentEditorClose(params: {
  documentKey: string | null | undefined
  item: FileItem | null | undefined
  elementId: number | null | undefined
}): R7EditorLifecycleCloseResult {
  const elementId = params.elementId != null ? Number(params.elementId) : NaN
  const item = params.item
  if (!item || !Number.isFinite(elementId) || elementId <= 0) {
    return { awaitingWs: false, session: null, restoredPhase: null }
  }

  const session = resolveEditingSessionForClose(params.documentKey, item, elementId)
  if (!session) {
    return { awaitingWs: false, session: null, restoredPhase: null }
  }

  const preOpen = session.preOpenDsPhase
  const closed = onEditorClosed(session.documentKey)
  if (!closed) {
    return { awaitingWs: false, session: null, restoredPhase: null }
  }

  if (closed.phase === 'awaiting_ws') {
    try {
      const store = getDsAttachmentStore(elementId)
      if (store.hasSnapshot(item)) {
        store.setPhase(item, 'awaiting_ws')
      }
    } catch {
      // ignore
    }
    return { awaitingWs: true, session: closed, restoredPhase: null }
  }

  const restoredPhase = restoreDsPhaseAfterCleanClose(item, elementId, preOpen)
  return { awaitingWs: false, session: closed, restoredPhase }
}
